import { useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { trpc } from "../../lib/trpc";
import { Icon } from "../ui/Icon";
import { DocChip } from "../shipment/DocChip";
import { FilePreviewModal } from "../shipment/FilePreviewModal";

/**
 * Arıza / bakım emri ekleri (fotoğraf + PDF) — DocChip grid + yükleme + önizleme + silme.
 * Metadata (yükleyen + tarih) gösterilir. Hedef: maintenanceOrderId VEYA faultReportId (tam biri).
 */
interface Props {
  maintenanceOrderId?: string | null;
  faultReportId?: string | null;
  canEdit: boolean;
  compact?: boolean;
}

function fmtDateTime(iso: string): string {
  return new Date(iso).toLocaleString("tr-TR", { dateStyle: "short", timeStyle: "short" });
}

export function AttachmentPanel({ maintenanceOrderId, faultReportId, canEdit, compact }: Props) {
  const qc = useQueryClient();
  const target = maintenanceOrderId ? { maintenanceOrderId } : { faultReportId: faultReportId ?? "" };
  const listQ = trpc.maintenance.attachment.list.useQuery(target, { enabled: !!(maintenanceOrderId || faultReportId), staleTime: 5_000 });
  const uploadMut = trpc.maintenance.attachment.upload.useMutation({ onSuccess: () => qc.invalidateQueries() });
  const removeMut = trpc.maintenance.attachment.remove.useMutation({ onSuccess: () => qc.invalidateQueries() });
  const fileRef = useRef<HTMLInputElement>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  function onPick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setErr(null);
    const reader = new FileReader();
    reader.onload = () => {
      uploadMut.mutate(
        { ...target, filename: file.name, dataBase64: String(reader.result) },
        { onError: (er) => setErr(er.message) },
      );
    };
    reader.readAsDataURL(file);
  }

  const rows = listQ.data ?? [];

  return (
    <div>
      {err ? <div className="form-banner-err" style={{ marginBottom: 8 }}>{err}</div> : null}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
        <span style={{ fontSize: 12.5, fontWeight: 600, color: "var(--neutral-800)" }}>Ekler ({rows.length})</span>
        <span style={{ flex: 1 }} />
        {canEdit ? (
          <>
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,application/pdf" style={{ display: "none" }} onChange={onPick} />
            <button type="button" className="btn-md secondary" onClick={() => fileRef.current?.click()} disabled={uploadMut.isPending}>
              <Icon name="upload" size={14} />{uploadMut.isPending ? "Yükleniyor…" : "Foto / PDF Ekle"}
            </button>
          </>
        ) : null}
      </div>
      {rows.length === 0 ? (
        <div style={{ padding: compact ? "8px 10px" : "12px 14px", border: "1px dashed var(--neutral-300)", borderRadius: "var(--radius-md)", background: "var(--neutral-50)", fontSize: 12, color: "var(--neutral-500)", textAlign: "center" }}>
          Henüz ek yok{canEdit ? " — fotoğraf veya PDF yükleyebilirsiniz" : ""}.
        </div>
      ) : (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
          {rows.map((a) => (
            <div key={a.id} style={{ display: "flex", flexDirection: "column", gap: 3 }}>
              <DocChip
                url={a.url}
                onOpen={setPreviewUrl}
                onRemove={canEdit ? () => removeMut.mutate({ id: a.id }) : undefined}
                busy={removeMut.isPending}
              />
              <span style={{ fontSize: 10, color: "var(--neutral-500)", maxWidth: 116, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={a.fileName ?? undefined}>
                {a.uploadedBy ? `${a.uploadedBy} · ` : ""}{fmtDateTime(a.createdAt)}
              </span>
            </div>
          ))}
        </div>
      )}
      <FilePreviewModal url={previewUrl} onClose={() => setPreviewUrl(null)} />
    </div>
  );
}
